import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { API } from "@/auth/AuthContext";
import { PHONE_DISPLAY, PHONE_TEL } from "@/components/shared";

const HERO_IMG = "https://images.unsplash.com/photo-1586024486164-ce9b3d87e09f?q=85&w=1600&auto=format&fit=crop";

export default function TVMountingPage() {
  const [opts, setOpts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/booking-options`).then((r) => setOpts(r.data)).catch(() => null);
  }, []);

  const minPrice = opts ? Math.min(...opts.tv_packages.map((t) => t.prices.install)) : 70;

  return (
    <>
      <Seo
        title="TV Mounting Prices & Online Booking | DuroTech28 DFW"
        description="See exact TV mounting prices, pick your package and book online in minutes. Serving Dallas–Fort Worth. Same-day when available. Call 469-897-6497."
        canonical="https://durotech28.me/tv-mounting"
      />
      <Nav />
      <main data-testid="tv-mounting-page">
        {/* Hero */}
        <header className="relative overflow-hidden border-b border-[#d4af37]/15">
          <img src={HERO_IMG} alt="Professionally mounted TV with concealed cables in a modern living room" className="absolute inset-0 w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0c]/70 via-[#0a0a0c]/85 to-[#0a0a0c]"></div>
          <div className="relative max-w-4xl mx-auto px-6 pt-20 pb-16 text-center">
            <span className="text-xs uppercase tracking-widest text-[#d4af37] font-mono">Dallas–Fort Worth · Low Voltage Specialist</span>
            <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tight text-white mt-4 leading-[0.98]">
              Professional<br />TV Mounting
            </h1>
            <p className="font-display text-2xl sm:text-3xl text-[#d4af37] mt-4">Starting at ${minPrice}</p>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mt-2">Same-Day Appointments When Available</p>
            <p className="text-sm text-zinc-300 max-w-xl mx-auto leading-relaxed mt-5">
              Professional TV installation for all sizes. Flat, tilting, and full-motion mounting
              options available. Cable concealment services available.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <Link to="/book" data-testid="tv-hero-book-btn"
                className="px-8 py-4 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-sm hover:opacity-95 transition-opacity shadow-lg shadow-[#d4af37]/25">
                Book TV Mounting →
              </Link>
              <a href={PHONE_TEL} data-testid="tv-hero-call-btn"
                className="px-8 py-4 rounded border border-zinc-600 text-white font-mono text-sm hover:border-[#d4af37] transition-colors">
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-6">
          {/* Price matrix */}
          <section className="py-14" data-testid="tv-pricing">
            <h2 className="font-display text-3xl uppercase text-white text-center">Transparent Package Pricing</h2>
            <p className="text-xs text-zinc-500 font-mono text-center mt-2 mb-8">Mounts matched to your TV's weight and VESA requirements.</p>
            {!opts ? (
              <p className="text-zinc-500 font-mono text-xs text-center">Loading prices...</p>
            ) : (
              <div className="rounded-2xl overflow-hidden gold-border">
                <table className="w-full text-xs sm:text-sm" data-testid="tv-price-table">
                  <thead>
                    <tr className="bg-[#121216] text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                      <th className="text-left p-3 sm:p-4">TV Size</th>
                      <th className="p-3 sm:p-4 text-right">Install Only</th>
                      <th className="p-3 sm:p-4 text-right">Flat Mount</th>
                      <th className="p-3 sm:p-4 text-right">Tilting</th>
                      <th className="p-3 sm:p-4 text-right">Full-Motion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opts.tv_packages.map((t) => (
                      <tr key={t.key} className="border-t border-zinc-800/60" data-testid={`tv-row-${t.key}`}>
                        <td className="p-3 sm:p-4 text-white font-medium">
                          {t.label}
                          {t.mount_included && <span className="block text-[9px] font-mono text-emerald-400 mt-0.5">Heavy-duty flat mount included</span>}
                        </td>
                        <td className="p-3 sm:p-4 text-right font-mono text-[#d4af37]">${t.prices.install}</td>
                        <td className="p-3 sm:p-4 text-right font-mono text-[#d4af37]">${t.prices.flat}</td>
                        <td className="p-3 sm:p-4 text-right font-mono text-[#d4af37]">${t.prices.tilting}</td>
                        <td className="p-3 sm:p-4 text-right font-mono text-[#d4af37]">${t.prices.full_motion}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-zinc-800/60">
                      <td className="p-3 sm:p-4 text-zinc-300" colSpan="5">Over 100" — <span className="font-mono text-zinc-400">Custom Quote</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Concealment */}
          {opts && (
            <section className="pb-14" data-testid="tv-concealment">
              <h2 className="font-display text-2xl uppercase text-white mb-6">Cable Concealment Options</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {opts.concealment_options.map((o) => (
                  <div key={o.key} className="rounded-xl bg-[#121216] gold-border p-5" data-testid={`tv-conceal-${o.key}`}>
                    <h3 className="text-sm font-semibold text-white">{o.name}</h3>
                    <p className={`font-display text-xl mt-2 ${o.kind === "custom" ? "text-amber-400" : "text-[#d4af37]"}`}>
                      {o.kind === "custom" ? `From $${o.price}` : `$${o.price}`}
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">{o.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-zinc-500 font-mono mt-4 leading-relaxed">
                Standard in-wall concealment covers appropriate low-voltage cabling only. New outlets, wiring, or circuit
                modifications are performed by appropriately licensed electricians — DuroTech28 coordinates with licensed partners.
              </p>
            </section>
          )}

          {/* How booking works */}
          <section className="pb-16" data-testid="tv-how-it-works">
            <h2 className="font-display text-2xl uppercase text-white mb-8 text-center">Booking Takes Two Minutes</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                ["Pick your TV size & mount", "See the exact package price before you book."],
                ["Add photos & details", "Upload wall photos so we arrive prepared."],
                ["Choose your appointment", "Live availability — same-day when open."],
                ["Small deposit, done", "Mount packages hold your slot with a deposit credited toward your total."],
              ].map(([t, d], i) => (
                <div key={t} className="rounded-xl bg-[#121216] border border-zinc-800 p-5">
                  <span className="font-display text-2xl text-[#d4af37]/40">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="text-sm font-semibold text-white mt-2 leading-snug">{t}</h3>
                  <p className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/book" data-testid="tv-bottom-book-btn"
                className="inline-block px-10 py-4 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-sm hover:opacity-95 transition-opacity shadow-lg shadow-[#d4af37]/25">
                Book TV Mounting →
              </Link>
              <p className="text-[10px] font-mono text-zinc-500 mt-3">Serving the Dallas–Fort Worth Metroplex and surrounding areas</p>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}
